/**
 * Standard Template Hooks
 * React Query hooks for managing standard template customization
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import config from "@/lib/config";

/**
 * Helper function for API calls with credentials
 */
async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = localStorage.getItem("auth-data");
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${config.apiUrl}${url}`, {
    ...options,
    credentials: "include",
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Request failed" }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

export interface StandardTemplateSettings {
  logoUrl?: string | null;
  logoWidth?: number;
  logoHeight?: number;
  fontSize?: number;
  fontColor?: string;
  tableColumns?: {
    description?: boolean;
    productType?: boolean;
    itemDate?: boolean;
    unit?: boolean;
    quantity?: boolean;
    unitPrice?: boolean;
    total?: boolean;
  };
}

const TEMPLATE_KEYS = {
  settings: () => ["standard-template", "settings"] as const,
  full: () => ["standard-template", "full"] as const,
};

/**
 * Get standard template settings
 */
export function useStandardTemplateSettings() {
  return useQuery({
    queryKey: TEMPLATE_KEYS.settings(),
    queryFn: async () => {
      console.log("Fetching standard template settings...");
      const response = await fetchWithAuth("/api/standard-template/settings");
      console.log("Settings response:", response);
      return response as StandardTemplateSettings;
    },
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get full standard template
 */
export function useStandardTemplate() {
  return useQuery({
    queryKey: TEMPLATE_KEYS.full(),
    queryFn: async () => {
      console.log("Fetching full standard template...");
      const response = await fetchWithAuth("/api/standard-template");
      console.log("Full template response:", response);
      return response;
    },
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Update standard template settings
 */
export function useUpdateStandardTemplateSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: StandardTemplateSettings) => {
      return fetchWithAuth("/api/standard-template/settings", {
        method: "PUT",
        body: JSON.stringify(settings),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMPLATE_KEYS.settings() });
      queryClient.invalidateQueries({ queryKey: TEMPLATE_KEYS.full() });
    },
  });
}

/**
 * Upload logo for standard template
 */
export function useUploadTemplateLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const token = localStorage.getItem("auth-data");
      const formData = new FormData();
      formData.append("logo", file);

      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`${config.apiUrl}/api/standard-template/upload-logo`, {
        method: "POST",
        body: formData,
        credentials: "include",
        headers,
      });

      if (!response.ok) {
        throw new Error("Failed to upload logo");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMPLATE_KEYS.settings() });
      queryClient.invalidateQueries({ queryKey: TEMPLATE_KEYS.full() });
    },
  });
}

/**
 * Reset standard template to defaults
 */
export function useResetStandardTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return fetchWithAuth("/api/standard-template/reset", {
        method: "POST",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMPLATE_KEYS.settings() });
      queryClient.invalidateQueries({ queryKey: TEMPLATE_KEYS.full() });
    },
  });
}
